import type { ThemeSeed } from './deriveTokens';
import type { ThemeCategory } from './tokens';

export interface ThemeSeedEntry {
  id: string;
  name: string;
  category: ThemeCategory;
  description: string;
  designedFor: 'light' | 'dark';
  sortOrder: number;
  seed: ThemeSeed;
}

/**
 * The preset catalogue. Ids are persisted in localStorage — never rename one;
 * a retired id simply falls back to the default.
 */
export const SEEDS: ThemeSeedEntry[] = [
  // ── Camptain ─────────────────────────────────────────────────────────────
  {
    // "Modern Academic Indigo" from the School360 design system document. The
    // brand, surface, text and status values are pinned so the default preset
    // matches the palette the app shipped with.
    id: 'camptain-indigo',
    name: 'Camptain Indigo',
    category: 'Camptain',
    description: 'The default. Academic indigo with a teal accent on cool, quiet greys.',
    designedFor: 'light',
    sortOrder: 1,
    seed: {
      primary: '#3949AB',
      primaryContrast: '#FFFFFF',
      secondary: '#00897B',
      accent: '#6573C3',
      neutralHue: '#4F5B93',
      neutralSaturation: 0.05,
      radius: { sm: 4, md: 6, lg: 8, pill: 999 },
      headingLetterSpacing: -0.2,
      modes: {
        light: {
          primary: { main: '#3949AB', light: '#6573C3', dark: '#283593', contrastText: '#FFFFFF' },
          secondary: {
            main: '#00897B',
            light: '#4DB6AC',
            dark: '#00695C',
            contrastText: '#FFFFFF',
          },
          status: {
            success: {
              main: '#2E7D32',
              light: '#4CAF50',
              dark: '#1B5E20',
              contrastText: '#FFFFFF',
            },
            warning: {
              main: '#ED6C02',
              light: '#FF9800',
              dark: '#C77700',
              contrastText: '#FFFFFF',
            },
            error: { main: '#D32F2F', light: '#EF5350', dark: '#C62828', contrastText: '#FFFFFF' },
            info: { main: '#1976D2', light: '#42A5F5', dark: '#1565C0', contrastText: '#FFFFFF' },
          },
          surface: { canvas: '#F7F8FC', paper: '#FFFFFF', raised: '#FFFFFF' },
          text: { primary: '#172033', secondary: '#667085' },
          border: { subtle: '#E2E5EC' },
        },
        dark: {
          primary: { main: '#7986CB', light: '#9FA8DA', dark: '#3949AB', contrastText: '#0F1013' },
          secondary: {
            main: '#4DB6AC',
            light: '#80CBC4',
            dark: '#00897B',
            contrastText: '#0F1013',
          },
          status: {
            success: {
              main: '#4CAF50',
              light: '#81C784',
              dark: '#2E7D32',
              contrastText: '#0F1013',
            },
            warning: {
              main: '#FFA726',
              light: '#FFB74D',
              dark: '#ED6C02',
              contrastText: '#0F1013',
            },
            error: { main: '#EF5350', light: '#E57373', dark: '#D32F2F', contrastText: '#0F1013' },
            info: { main: '#42A5F5', light: '#64B5F6', dark: '#1976D2', contrastText: '#0F1013' },
          },
          surface: { canvas: '#10131C', paper: '#171B27', raised: '#1D2231', sunken: '#0D1017' },
          text: { primary: '#E7E9F0', secondary: '#9AA1B4' },
          border: { subtle: 'rgba(231, 233, 240, 0.12)' },
        },
      },
    },
  },
  {
    id: 'camptain-emerald',
    name: 'Emerald',
    category: 'Camptain',
    description: 'Deep green with warm, sand-tinted greys and a gold secondary.',
    designedFor: 'light',
    sortOrder: 2,
    seed: {
      primary: '#006C35',
      primaryContrast: '#FFFFFF',
      secondary: '#C9963C',
      accent: '#52B788',
      neutralHue: '#8C7A5E',
      neutralSaturation: 0.05,
    },
  },
  {
    id: 'camptain-sapphire',
    name: 'Sapphire',
    category: 'Camptain',
    description: 'Institutional blue. Clear and trusted.',
    designedFor: 'light',
    sortOrder: 3,
    seed: {
      primary: '#1565C0',
      primaryContrast: '#FFFFFF',
      accent: '#64B5F6',
      neutralHue: '#64748B',
      neutralSaturation: 0.04,
    },
  },
  {
    id: 'camptain-teal',
    name: 'Teal',
    category: 'Camptain',
    description: 'Calm and low-arousal. Built for screens people sit in front of all day.',
    designedFor: 'light',
    sortOrder: 4,
    seed: {
      primary: '#00796B',
      primaryContrast: '#FFFFFF',
      accent: '#4DB6AC',
      neutralHue: '#5E7C79',
      neutralSaturation: 0.04,
    },
  },
  {
    id: 'camptain-violet',
    name: 'Violet',
    category: 'Camptain',
    description: 'Precise and slightly cool. Suits science and research departments.',
    designedFor: 'light',
    sortOrder: 5,
    seed: {
      primary: '#5E35B1',
      primaryContrast: '#FFFFFF',
      accent: '#9575CD',
      neutralHue: '#6B6480',
      neutralSaturation: 0.05,
    },
  },

  // ── Enterprise ─────────────────────────────────────────────────────────────
  {
    id: 'enterprise-modern',
    name: 'Modern Enterprise',
    category: 'Enterprise',
    description: 'Indigo on cool grey, dark sidebar, striped tables. For dense admin work.',
    designedFor: 'light',
    sortOrder: 1,
    seed: {
      primary: '#3B4FD8',
      primaryContrast: '#FFFFFF',
      secondary: '#1E2A78',
      accent: '#5B8DEF',
      neutralHue: '#64748B',
      neutralSaturation: 0.06,
      darkSidebar: true,
      stripedTables: true,
      radius: { sm: 4, md: 6, lg: 8, pill: 999 },
    },
  },
  {
    id: 'enterprise-fluent',
    name: 'Fluent Blue',
    category: 'Enterprise',
    description: 'Communication blue with near-neutral greys and restrained corners.',
    designedFor: 'light',
    sortOrder: 2,
    seed: {
      primary: '#0F6CBD',
      primaryContrast: '#FFFFFF',
      secondary: '#243A5E',
      accent: '#2899F5',
      neutralHue: '#605E5C',
      neutralSaturation: 0.02,
      radius: { sm: 4, md: 6, lg: 8, pill: 999 },
      fontFamily: '"Segoe UI", "Inter", system-ui, sans-serif',
      headingLetterSpacing: 0,
      elevationStrength: 0.9,
    },
  },

  // ── Premium Dark ───────────────────────────────────────────────────────────
  {
    id: 'premium-dark-obsidian',
    name: 'Obsidian',
    category: 'Premium Dark',
    description: 'Near-black with a cool cast and a single bright accent. Best in dark mode.',
    designedFor: 'dark',
    sortOrder: 1,
    seed: {
      primary: '#4C8DFF',
      primaryContrast: '#06111F',
      secondary: '#1E3A5F',
      accent: '#38BDF8',
      neutralHue: '#5A6478',
      neutralSaturation: 0.07,
      radius: { sm: 4, md: 6, lg: 8, pill: 999 },
      elevationStrength: 1.1,
    },
  },

  // ── High-Contrast ──────────────────────────────────────────────────────────
  {
    // The accessibility fallback: every value is chosen for contrast, not taste.
    id: 'high-contrast',
    name: 'High Contrast',
    category: 'High-Contrast',
    description: 'Maximum legibility. Pure surfaces, solid borders, no shadow or tint.',
    designedFor: 'light',
    sortOrder: 1,
    seed: {
      primary: '#00379B',
      primaryContrast: '#FFFFFF',
      secondary: '#000000',
      accent: '#00379B',
      neutralHue: '#000000',
      neutralSaturation: 0,
      radius: { sm: 2, md: 4, lg: 4, pill: 999 },
      elevationStrength: 0,
      headingLetterSpacing: 0,
      modes: {
        dark: {
          primary: { main: '#7CB7FF', contrastText: '#000000' },
          secondary: { main: '#FFFFFF', contrastText: '#000000' },
          surface: { canvas: '#000000', paper: '#0A0A0A' },
          text: { primary: '#FFFFFF', secondary: '#D6D6D6' },
        },
      },
    },
  },
];
